'use client';

import { useState } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { Box, Button, Container, TextField, Card, Alert, IconButton, InputAdornment } from '@mui/material';
import { useBoolean } from 'minimal-shared/hooks';
import { Iconify } from 'src/components/iconify';
import { useAuthContext } from '@/src/auth/hooks';
import { CONFIG } from '@/src/global-config';
import { paths } from 'src/routes/paths';
import { RouterLink } from 'src/routes/components';
import { LokaltKontoSaknasFel } from 'src/lib/localAuth';
import { RegistreraKontoSektion } from '@/src/auth/components/registrera-konto-sektion';

export function LocalInloggningView() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const epostFrånRegistreringen = searchParams.get('email') || '';
  // LocalAuthProvider exposes register/login beyond the shared AuthContextValue type.
  const { login } = useAuthContext() as any;
  const [email, setEmail] = useState(epostFrånRegistreringen);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const showPassword = useBoolean();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      router.push(CONFIG.auth.redirectPath);
    } catch (err: any) {
      // Finns inget konto på adressen är registrering enda vägen vidare — skicka dit direkt
      // med adressen i släptåg i stället för att visa ett fel som bara ber om ett nytt försök.
      if (err instanceof LokaltKontoSaknasFel) {
        router.push(`${paths.REGISTRERA}?email=${encodeURIComponent(email)}`);
        return;
      }
      setError(err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ py: 12 }}>
      <Card sx={{ p: 4 }}>
        <h1>Logga in</h1>

        {epostFrånRegistreringen && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <strong>{epostFrånRegistreringen}</strong> har redan ett konto. Logga in istället.
          </Alert>
        )}

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Box component="form" onSubmit={handleLogin} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            fullWidth
          />

          <TextField
            label="Password"
            type={showPassword.value ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            fullWidth
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={showPassword.onToggle}
                      edge="end"
                      aria-label={showPassword.value ? 'Dölj lösenord' : 'Visa lösenord'}
                    >
                      <Iconify
                        icon={showPassword.value ? 'solar:eye-bold' : 'solar:eye-closed-bold'}
                      />
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
          />

          {/* Samma plats som i Firebase-vyn: under lösenordsfältet, före inloggningsknappen.
              E-posten följer med i länken så återställningsvyn slipper fråga igen. */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              component={RouterLink}
              href={email ? `${paths.ÅTERSTÄLL}?email=${encodeURIComponent(email)}` : paths.ÅTERSTÄLL}
              variant="text"
              size="small"
            >
              Glömt lösenordet?
            </Button>
          </Box>

          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={loading}
          >
            {loading ? 'Logging in...' : 'Logga In'}
          </Button>
        </Box>

        <Box sx={{ mt: 2, textAlign: 'center', fontSize: '12px', color: 'text.secondary' }}>
          ✅ Använd lokal autentisering (ingen Firebase)
        </Box>
      </Card>

      <RegistreraKontoSektion sx={{ mt: 3 }} />
    </Container>
  );
}
